import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMemberAuth } from '../../hooks/useMemberAuth';
import { updateMemberProfile, uploadMemberPhoto } from '../../services/member-auth.service';
import Button from '../../components/common/Button';
import PhotoUpload from '../../components/forms/PhotoUpload';
import { OBRIGACOES_OPTIONS } from '../../constants/obrigacoes';
import { CheckCircle, AlertCircle } from 'lucide-react';

const MemberProfile = () => {
  const { member, updateMember } = useMemberAuth();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  const handlePhotoUpload = async () => {
    setPhotoError('');
    if (!photoFile) {
      setPhotoError('Selecione uma imagem primeiro.');
      return;
    }
    setPhotoLoading(true);
    try {
      const response = await uploadMemberPhoto(photoFile);
      updateMember(response.member);
      setPhotoFile(null);
      setSuccess(true);
    } catch (err) {
      setPhotoError(err.message || 'Erro ao enviar foto.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: member?.name || '',
      phone: member?.phone || '',
      birth_date: member?.birth_date || '',
      feitura_date: member?.feitura_date || '',
      idade_de_santo: member?.idade_de_santo ?? '',
      orixa: member?.orixa || '',
      junto: member?.junto || '',
      obrigacoes_pagas: member?.obrigacoes_pagas || [],
    },
  });

  const password = watch('password');
  const obrigacoesSelecionadas = watch('obrigacoes_pagas') || [];

  const toggleObrigacao = (obrigacao) => {
    const current = getValues('obrigacoes_pagas') || [];
    if (current.includes(obrigacao)) {
      setValue('obrigacoes_pagas', current.filter((o) => o !== obrigacao));
    } else {
      setValue('obrigacoes_pagas', [...current, obrigacao]);
    }
  };

  const onSubmit = async (data) => {
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      const payload = {
        name: data.name,
        phone: data.phone || null,
        birth_date: data.birth_date || null,
        feitura_date: data.feitura_date || null,
        idade_de_santo: data.idade_de_santo !== '' ? parseInt(data.idade_de_santo, 10) : null,
        orixa: data.orixa || null,
        junto: data.junto || null,
        obrigacoes_pagas: data.obrigacoes_pagas || [],
      };

      if (data.password) {
        payload.password = data.password;
        payload.password_confirmation = data.password_confirmation;
      }

      const response = await updateMemberProfile(payload);

      // Atualiza o localStorage com os novos dados
      localStorage.setItem('member_user', JSON.stringify(response.member));

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Erro ao atualizar perfil.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-2xl font-display font-bold text-brand-brown">Meu Perfil</h2>
        <p className="text-gray-500 mt-1">Atualize seus dados cadastrais</p>
      </div>

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-700">Perfil atualizado com sucesso!</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Foto */}
      <div className="bg-white rounded-xl border-2 border-brand-beige p-6 shadow-sm space-y-4">
        <h3 className="font-semibold text-brand-brown">Foto</h3>
        <PhotoUpload
          value={photoFile}
          onChange={setPhotoFile}
          existingUrl={member?.photo_url || null}
          error={photoError}
          label=""
        />
        {photoFile && (
          <Button
            type="button"
            variant="primary"
            loading={photoLoading}
            onClick={handlePhotoUpload}
          >
            Salvar foto
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border-2 border-brand-beige p-6 shadow-sm">
        <h3 className="font-semibold text-brand-brown mb-4">Informações Pessoais</h3>

        {/* Read-only info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Email</p>
            <p className="text-sm text-gray-700 font-medium">{member?.email}</p>
          </div>
          {member?.cpf && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">CPF</p>
              <p className="text-sm text-gray-700 font-medium">{member.cpf}</p>
            </div>
          )}
          {member?.registration_number && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Matrícula</p>
              <p className="text-sm text-gray-700 font-medium">{member.registration_number}</p>
            </div>
          )}
          {member?.affiliation_date && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Associado desde</p>
              <p className="text-sm text-gray-700 font-medium">
                {new Date(member.affiliation_date).toLocaleDateString('pt-BR')}
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label htmlFor="name" className="label">Nome completo</label>
            <input
              id="name"
              type="text"
              className={`input ${errors.name ? 'input-error' : ''}`}
              {...register('name', {
                required: 'Nome é obrigatório',
                minLength: { value: 2, message: 'Nome deve ter no mínimo 2 caracteres' },
              })}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="phone" className="label">Telefone</label>
            <input
              id="phone"
              type="text"
              className="input"
              placeholder="(31) 99999-9999"
              {...register('phone')}
            />
          </div>

          <div>
            <label htmlFor="birth_date" className="label">Data de nascimento</label>
            <input
              id="birth_date"
              type="date"
              className="input"
              {...register('birth_date')}
            />
          </div>

          <hr className="border-brand-beige" />

          <h3 className="font-semibold text-brand-brown">Dados de Candomblé</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="feitura_date" className="label">Data de feitura</label>
              <input
                id="feitura_date"
                type="date"
                className="input"
                {...register('feitura_date')}
              />
            </div>

            <div>
              <label htmlFor="idade_de_santo" className="label">Idade de santo (anos)</label>
              <input
                id="idade_de_santo"
                type="number"
                min="0"
                className="input"
                {...register('idade_de_santo', {
                  min: { value: 0, message: 'Idade de santo não pode ser negativa' },
                })}
              />
              {errors.idade_de_santo && (
                <p className="mt-1 text-sm text-red-600">{errors.idade_de_santo.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="orixa" className="label">Orixá</label>
              <input
                id="orixa"
                type="text"
                className="input"
                {...register('orixa')}
              />
            </div>

            <div>
              <label htmlFor="junto" className="label">Juntó</label>
              <input
                id="junto"
                type="text"
                className="input"
                {...register('junto')}
              />
            </div>
          </div>

          <div>
            <label className="label">Obrigações pagas</label>
            <div className="mt-2 p-4 rounded-lg border border-gray-200 bg-gray-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OBRIGACOES_OPTIONS.map((obrigacao) => (
                  <label key={obrigacao} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-brand-brown border-gray-300 rounded focus:ring-brand-brown cursor-pointer"
                      checked={obrigacoesSelecionadas.includes(obrigacao)}
                      onChange={() => toggleObrigacao(obrigacao)}
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {obrigacao}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <hr className="border-brand-beige" />

          <h3 className="font-semibold text-brand-brown">Alterar Senha</h3>
          <p className="text-sm text-gray-500 -mt-3">Deixe em branco para manter a senha atual</p>

          <div>
            <label htmlFor="password" className="label">Nova senha</label>
            <input
              id="password"
              type="password"
              className={`input ${errors.password ? 'input-error' : ''}`}
              {...register('password', {
                minLength: { value: 6, message: 'Senha deve ter no mínimo 6 caracteres' },
              })}
            />
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <div>
            <label htmlFor="password_confirmation" className="label">Confirmar nova senha</label>
            <input
              id="password_confirmation"
              type="password"
              className={`input ${errors.password_confirmation ? 'input-error' : ''}`}
              {...register('password_confirmation', {
                validate: (value) =>
                  !password || value === password || 'As senhas não conferem',
              })}
            />
            {errors.password_confirmation && (
              <p className="mt-1 text-sm text-red-600">{errors.password_confirmation.message}</p>
            )}
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
            >
              Salvar Alterações
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberProfile;
